import React, { useState, useEffect, useCallback } from "react";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import {
  Loader2, X, Download, Maximize2, Minimize2,
  Activity, ListChecks, Target, Shield, Milestone as MilestoneIcon,
  GitPullRequest, MessageSquare, CalendarCheck, Mail, Zap, Tag,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { contractStatusColors, formatDate, formatCurrency } from "./constants";
import { ContractHealthPanel } from "../contract-detail/ContractHealthPanel";
import { DecisionIntelligenceTab } from "../contract-detail/DecisionIntelligenceTab";
import { PerformanceTab } from "./PerformanceTab";
import { DeliverablesTab } from "./DeliverablesTab";
import { KpisTab } from "./KpisTab";
import { ObligationsTab } from "./ObligationsTab";
import { MilestonesTab } from "./MilestonesTab";
import { ChangesTab } from "./ChangesTab";
import { NotesTab } from "./NotesTab";
import { ReviewsTab } from "./ReviewsTab";
import { EmailsTab } from "./EmailsTab";

export function ContractManagementPanel({ contractId, onClose, expanded, onToggleExpand }) {
  const { user, canDo } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("performance");
  const [obligations, setObligations] = useState([]);
  const [milestones, setMilestones] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const resp = await apiClient.get(`/contracts/${contractId}/management-summary`);
      setData(resp.data);
      const [obRes, msRes] = await Promise.allSettled([
        apiClient.get(`/obligations?contract_id=${contractId}`),
        apiClient.get(`/milestones?contract_id=${contractId}`),
      ]);
      if (obRes.status === "fulfilled") setObligations(obRes.value.data || []);
      if (msRes.status === "fulfilled") setMilestones(msRes.value.data || []);
    } catch (e) {
      toast.error("Failed to load contract data");
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const submitForm = async (endpoint, payload, resetFn, hideFormFn) => {
    try {
      const params = new URLSearchParams(payload).toString();
      await apiClient.post(`${endpoint}?${params}`);
      toast.success("Saved successfully");
      resetFn();
      hideFormFn(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const updateItem = async (endpoint, payload) => {
    try {
      const params = new URLSearchParams(payload).toString();
      await apiClient.put(`${endpoint}?${params}`);
      toast.success("Updated");
      fetchData();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const updateComplianceItem = async (type, itemId, body) => {
    try {
      await apiClient.put(`/${type}/${itemId}`, body);
      toast.success("Status updated");
      fetchData();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const updateDeliverableStatus = async (idx, newStatus) => {
    try {
      const cf = { ...data.contract.custom_fields };
      const delivs = [...(cf.deliverables || [])];
      delivs[idx] = { ...delivs[idx], tracking_status: newStatus };
      if (newStatus === "delivered") delivs[idx].delivered_at = new Date().toISOString();
      cf.deliverables = delivs;
      await apiClient.put(`/contracts/${contractId}`, { custom_fields: cf });
      toast.success("Deliverable status updated");
      fetchData();
    } catch {
      toast.error("Failed to update");
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-slate-400" size={32} /></div>;
  if (!data) return null;

  const contract = data.contract;
  const canLog = canDo("management.log_performance", contract?.created_by, contract?.contract_owner_id);

  return (
    <div className={`space-y-4 ${expanded ? "h-full flex flex-col" : ""}`} data-testid="contract-management-panel">
      {/* Contract Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <h2 className={`font-semibold text-slate-900 dark:text-white ${expanded ? "text-xl" : "text-lg"}`}>{contract.title}</h2>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <Badge className={`rounded-sm ${contractStatusColors[contract.status]}`}>{contract.status}</Badge>
            <span className="text-sm text-slate-500">{contract.contract_type}</span>
            <span className="text-sm text-slate-500">{contract.contracting_party?.legal_name}</span>
            {contract.spend_category && <Badge variant="outline" className="text-xs gap-1"><Tag size={10} />{contract.spend_category}</Badge>}
            {contract.contract_value && <span className="text-sm font-mono text-slate-600">{formatCurrency(contract.contract_value, contract.currency)}</span>}
          </div>
          <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
            <span>Created by: {contract.creator_name}</span>
            <span>Effective: {formatDate(contract.effective_date)}</span>
            <span>Expires: {formatDate(contract.expiration_date)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {(user?.role === "superuser" || user?.id === contract.created_by) && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 rounded-sm text-xs h-8"
              data-testid="download-report-btn"
              onClick={() => {
                window.open(`${process.env.REACT_APP_BACKEND_URL}/api/contracts/${contractId}/management-report`, "_blank");
              }}
            >
              <Download size={14} />
              Download Report
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={onToggleExpand} className="h-8 w-8" data-testid="expand-panel-btn" title={expanded ? "Collapse panel" : "Expand to full screen"}>
            {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </Button>
          <Button variant="ghost" size="icon" onClick={expanded ? onToggleExpand : onClose} className="h-8 w-8" data-testid="close-panel-btn"><X size={16} /></Button>
        </div>
      </div>

      {/* Health Panel */}
      {["active", "approved"].includes(contract.status) && (
        <ContractHealthPanel contractId={contractId} onNavigateTab={setActiveTab} />
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className={expanded ? "flex-1 flex flex-col min-h-0" : ""}>
        <TabsList className="w-full grid grid-cols-10 h-9">
          <TabsTrigger value="performance" className="text-xs gap-1" data-testid="performance-tab-trigger"><Activity size={13} />Performance</TabsTrigger>
          <TabsTrigger value="deliverables" className="text-xs gap-1" data-testid="deliverables-tab-trigger"><ListChecks size={13} />Deliverables</TabsTrigger>
          <TabsTrigger value="kpis" className="text-xs gap-1" data-testid="kpis-tab-trigger"><Target size={13} />KPIs</TabsTrigger>
          <TabsTrigger value="obligations" className="text-xs gap-1" data-testid="obligations-tab-trigger"><Shield size={13} />Obligations</TabsTrigger>
          <TabsTrigger value="milestones" className="text-xs gap-1" data-testid="milestones-tab-trigger"><MilestoneIcon size={13} />Milestones</TabsTrigger>
          <TabsTrigger value="changes" className="text-xs gap-1" data-testid="changes-tab-trigger"><GitPullRequest size={13} />Changes</TabsTrigger>
          <TabsTrigger value="notes" className="text-xs gap-1" data-testid="notes-tab-trigger"><MessageSquare size={13} />Notes</TabsTrigger>
          <TabsTrigger value="reviews" className="text-xs gap-1" data-testid="reviews-tab-trigger"><CalendarCheck size={13} />Reviews</TabsTrigger>
          <TabsTrigger value="emails" className="text-xs gap-1" data-testid="emails-tab-trigger"><Mail size={13} />Emails</TabsTrigger>
          <TabsTrigger value="decisions" className="text-xs gap-1" data-testid="decisions-tab-trigger"><Zap size={13} className="text-amber-500" />Decisions</TabsTrigger>
        </TabsList>

        <TabsContent value="performance" className="space-y-3 mt-3">
          <PerformanceTab
            contractId={contractId}
            performanceLogs={data.performance_logs || []}
            fetchData={fetchData}
            expanded={expanded}
            canLogPerformance={canLog}
            submitForm={submitForm}
          />
        </TabsContent>

        <TabsContent value="deliverables" className="space-y-3 mt-3" data-testid="deliverables-tab">
          <DeliverablesTab
            deliverables={contract?.custom_fields?.deliverables || []}
            expanded={expanded}
            onUpdateStatus={updateDeliverableStatus}
          />
        </TabsContent>

        <TabsContent value="kpis" className="space-y-3 mt-3">
          <KpisTab
            contractKpis={data.contract_kpis || []}
            contractId={contractId}
            fetchData={fetchData}
            expanded={expanded}
          />
        </TabsContent>

        <TabsContent value="obligations" className="space-y-3 mt-3">
          <ObligationsTab
            obligations={obligations}
            expanded={expanded}
            onUpdateStatus={updateComplianceItem}
          />
        </TabsContent>

        <TabsContent value="milestones" className="space-y-3 mt-3">
          <MilestonesTab
            milestones={milestones}
            expanded={expanded}
            onUpdateStatus={updateComplianceItem}
          />
        </TabsContent>

        <TabsContent value="changes" className="space-y-3 mt-3">
          <ChangesTab
            contractId={contractId}
            changes={data.change_control || []}
            fetchData={fetchData}
            expanded={expanded}
            submitForm={submitForm}
            updateItem={updateItem}
          />
        </TabsContent>

        <TabsContent value="notes" className="space-y-3 mt-3">
          <NotesTab
            contractId={contractId}
            notes={data.notes || []}
            fetchData={fetchData}
            expanded={expanded}
            canAddNote={canLog}
          />
        </TabsContent>

        <TabsContent value="reviews" className="space-y-3 mt-3">
          <ReviewsTab
            contractId={contractId}
            reviews={data.review_dates || []}
            fetchData={fetchData}
            expanded={expanded}
            submitForm={submitForm}
            updateItem={updateItem}
          />
        </TabsContent>

        <TabsContent value="emails" className="space-y-3 mt-3">
          <EmailsTab
            contractId={contractId}
            emailLogs={data.email_logs || []}
            fetchData={fetchData}
            expanded={expanded}
          />
        </TabsContent>

        <TabsContent value="decisions" className="space-y-3 mt-3">
          <DecisionIntelligenceTab contractId={contractId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
